import { useEffect, useState } from "react";
import {
  getInitialTheme,
  setTheme as persistTheme,
  type Theme,
} from "../lib/theme";

/**
 * Reactive theme state. Reads the current theme on mount and exposes
 * a setter that both applies the class on <html> and persists to
 * localStorage.
 */
export function useTheme(): [Theme, (next: Theme) => void, () => void] {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme());

  // Keep state in sync with system-preference changes (user hasn't set
  // a manual override — honour OS). Only applies before the user has
  // explicitly picked a theme via the toggle.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      if (window.localStorage.getItem("cutmate:theme")) return; // user override
      const next: Theme = e.matches ? "dark" : "light";
      persistTheme(next);
      setThemeState(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const set = (next: Theme) => {
    persistTheme(next);
    setThemeState(next);
  };

  const toggle = () => set(theme === "dark" ? "light" : "dark");

  return [theme, set, toggle];
}
